import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from '../utils/toast';
import { Plus, Shield } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StaffAccountsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [staffAccounts, setStaffAccounts] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  
  useEffect(() => {
    fetchData();
  }, []);
  
  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [empRes, staffRes] = await Promise.all([
        axios.get(`${API}/hospital/employees`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/hospital/staff/accounts`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setEmployees(empRes.data.employees);
      setStaffAccounts(staffRes.data.staff_accounts);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };
  
  const employeesWithoutAccounts = employees.filter(
    emp => !staffAccounts.find(sa => sa.employee_id === emp.employee_id)
  );
  
  const handleCreateAccounts = async () => {
    if (selectedEmployees.length === 0) {
      toast.error('Please select at least one employee');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const results = [];
      
      for (const empId of selectedEmployees) {
        const res = await axios.post(
          `${API}/hospital/staff/create-account`,
          { employee_id: empId, auto_generate_password: true },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        results.push(res.data);
      }
      
      toast.success(`Created ${results.length} staff accounts`);
      setSelectedEmployees([]);
      setShowCreateDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create accounts');
    } finally {
      setLoading(false);
    }
  };
  
  const handleToggleManager = async (employeeId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/hospital/staff/${employeeId}/set-reporting-manager`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { is_manager: !currentStatus }
        }
      );
      toast.success('Reporting manager status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };
  
  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[#0056B3] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }
  
  return (
    <AdminLayout>
      <div className="p-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">STAFF ACCOUNTS MANAGEMENT</h1>
      
        <div className="space-y-8">
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-[18px] font-semibold text-gray-900">Active Staff Accounts ({staffAccounts.length})</h2>
              <Button onClick={() => setShowCreateDialog(true)} className="bg-[#0056B3] hover:bg-[#0056B3]/90">
                <Plus className="w-4 h-4 mr-2" />Create Staff Accounts
              </Button>
            </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Department</TableHead>
                <TableHead>Job Role</TableHead>
                <TableHead>Reporting Manager</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {staffAccounts.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-8 text-gray-500">No staff accounts yet</TableCell></TableRow>
              ) : (
                staffAccounts.map(staff => (
                  <TableRow key={staff.employee_id}>
                    <TableCell className="font-medium">{staff.full_name}</TableCell>
                    <TableCell>{staff.email}</TableCell>
                    <TableCell>{staff.department_name}</TableCell>
                    <TableCell>{staff.job_function_name}</TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleManager(staff.employee_id, staff.is_reporting_manager)}
                      >
                        {staff.is_reporting_manager ? (
                          <Badge className="bg-blue-100 text-blue-800"><Shield className="w-3 h-3 mr-1" />Manager</Badge>
                        ) : (
                          <span className="text-gray-500">Set as Manager</span>
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Create Staff Accounts</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">Select employees to create staff user accounts:</p>
            <div className="max-h-96 overflow-y-auto space-y-2">
              {employeesWithoutAccounts.length === 0 ? (
                <p className="text-center py-4 text-gray-500">All employees have staff accounts</p>
              ) : (
                employeesWithoutAccounts.map(emp => (
                  <div key={emp.employee_id} className="flex items-center space-x-3 p-3 border rounded hover:bg-gray-50">
                    <Checkbox
                      checked={selectedEmployees.includes(emp.employee_id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedEmployees([...selectedEmployees, emp.employee_id]);
                        } else {
                          setSelectedEmployees(selectedEmployees.filter(id => id !== emp.employee_id));
                        }
                      }}
                    />
                    <div className="flex-1">
                      <p className="font-medium">{emp.full_name}</p>
                      <p className="text-sm text-gray-600">{emp.email} • {emp.department_name}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="flex space-x-2">
              <Button onClick={() => setShowCreateDialog(false)} variant="outline" className="flex-1">Cancel</Button>
              <Button onClick={handleCreateAccounts} className="flex-1 bg-[#0056B3]" disabled={selectedEmployees.length === 0}>Create {selectedEmployees.length} Accounts</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
        </div>
      </div>
    </AdminLayout>
  );
};

export default StaffAccountsPage;